import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Divider,
  Card,
  CardContent,
  IconButton,
  Tabs,
  Tab,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Snackbar,
  InputAdornment,
  Autocomplete,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  List,
  ListItem,
  ListItemText,
  FormControl,
  Select,
  Tooltip,
  useMediaQuery,
  useTheme,
  Stack,
  Checkbox,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MapIcon from '@mui/icons-material/Map';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PrintIcon from '@mui/icons-material/Print';
import CalculateIcon from '@mui/icons-material/Calculate';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import MessageIcon from '@mui/icons-material/Message';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MarkChatReadIcon from '@mui/icons-material/MarkChatRead';
import Badge from '@mui/material/Badge';
import Collapse from '@mui/material/Collapse';
import type { Job, User, Quote, JobNotesFormula } from '../../types';
import QuoteComponent from './Quote';
import Files from './Files';
import Product from './Product';
import History from './History';
import ChatPanel from '../../components/ChatPanel';
import { formatDateForDisplay, formatDateForInput, formatRelativeTimeInNY } from '../../utils/dateUtils';
import { apiRequest, api } from '../../utils/api';
import { formatPhoneInput, getWhatsAppLink } from '../../utils/phoneUtils';
import { printBarcodes } from '../../utils/printUtils';
import AddressMapModal from '../../components/AddressMapModal';

interface JobDetailProps {
  jobId: number;
  onBack: () => void;
  onEdit?: (id: number) => void;
  getJobById?: (id: number) => Job | undefined;
}


const statuses: Job['status'][] = [
  'Estimate',
  'Proposal',
  'Measure',
  'Shop Drawing',
  'Fabrication',
  'Install',
  'Finish',
];

interface Customer {
  id: number;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
}

// Helper function to format decimal inches as fractional inches (e.g. 63.25 => 63-1/4")
const formatInches = (value: number): string => {
  const whole = Math.floor(value);
  const fracOf16 = Math.round((value - whole) * 16);
  const fractionMap: Record<number, string> = {
    1: '1/16', 2: '1/8', 3: '3/16', 4: '1/4', 5: '5/16',
    6: '3/8', 7: '7/16', 8: '1/2', 9: '9/16', 10: '5/8',
    11: '11/16', 12: '3/4', 13: '13/16', 14: '7/8', 15: '15/16',
  };
  const fracStr = fractionMap[fracOf16] || '';
  if (!fracStr) return `${whole}"`;
  return whole > 0 ? `${whole}-${fracStr}"` : `${fracStr}"`;
};

const formatSize = (width: number | string | null | undefined, height: number | string | null | undefined): string => {
  if (!width || !height) return 'N/A';
  return `${formatInches(parseFloat(String(width)))} x ${formatInches(parseFloat(String(height)))}`;
};

// Helper functions for time conversion
const convertTo24Hour = (time12h: string): string => {
  if (!time12h || !time12h.includes(' ')) {
    // If already in 24-hour format or invalid, return as is
    if (time12h && time12h.includes(':')) {
      return time12h.split(' ')[0]; // Remove AM/PM if present
    }
    return '09:00';
  }
  const [time, modifier] = time12h.split(' ');
  let [hours, minutes] = time.split(':');
  if (hours === '12') {
    hours = '00';
  }
  if (modifier === 'PM') {
    hours = String(parseInt(hours, 10) + 12);
  }
  return `${hours.padStart(2, '0')}:${minutes || '00'}`;
};

const convertTo12Hour = (time24h: string): string => {
  if (!time24h) return '09:00 AM';
  const [hours, minutes] = time24h.split(':');
  const hour24 = parseInt(hours, 10);
  if (isNaN(hour24)) return '09:00 AM';
  const hour12 = hour24 % 12 || 12;
  const modifier = hour24 >= 12 ? 'PM' : 'AM';
  return `${hour12}:${minutes || '00'} ${modifier}`;
};




interface Room {
  id: number;
  job_id: number;
  room_name: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface ProductDetail {
  id: number;
  product_id: number;
  job_id: number;
  product_type_id?: number | null;
  width?: number | null;
  height?: number | null;
  number?: number | null;
  status?: string | null;
  barcode?: string | null;
  type_of_work?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

interface Product {
  id: number;
  product_name: string;
  room?: string | null;
  status?: string | null;
  note?: string | null;
  product_type_id?: number | null;
  order?: number | null;
  job_id: number;
  created_at?: string | null;
  updated_at?: string | null;
  details?: ProductDetail[];
}

interface ProductType {
  id: number;
  productType: string;
  description?: string | null;
  sellingPrice?: number | null;
  unit?: string | null;
}

interface RoomStatusGroup {
  status: string;
  amount: number;
  rooms: Room[];
}

interface ProductStatusGroup {
  status: string;
  amount: number;
  products: Product[];
}

interface MaterialStatusGroup {
  status: string;
  amount: number;
  details: ProductDetail[];
}

export default function JobDetail({
  jobId,
  onBack,
  getJobById,
}: JobDetailProps) {
  const [job, setJob] = useState<Job | null>(null);
  const [customerEmail, setCustomerEmail] = useState<string>('');
  const [customerPhone, setCustomerPhone] = useState<string>('');
  const [companyName, setCompanyName] = useState<string>('');
  const [contactPerson, setContactPerson] = useState<string>('');
  // Check URL hash for tab navigation (e.g., #chat)
  const getInitialTab = () => {
    if (typeof window !== 'undefined') {
      const hash = window.location.hash;
      if (hash === '#chat') return 5; // Chat tab
      if (hash === '#quote') {
        // Field workers should not access quote tab
        // This will be handled by the useEffect, but default to 0 here
        return 0;
      }
      if (hash === '#files') return 2;
      if (hash === '#product') return 3;
      if (hash === '#history') {
        // Field workers should not access history tab
        return 0;
      }
    }
    return 0; // Default to Detail tab
  };
  const [activeTab, setActiveTab] = useState(getInitialTab());
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [productTypes, setProductTypes] = useState<ProductType[]>([]);
  const [productDetailStatuses, setProductDetailStatuses] = useState<Array<{ id: number; statusName: string; isActive: boolean }>>([]);
  const [typeOfWorkList, setTypeOfWorkList] = useState<string[]>([]);
  const [statusListsLoading, setStatusListsLoading] = useState(false);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [roomDetailDialogOpen, setRoomDetailDialogOpen] = useState(false);
  const [productDetailDialogOpen, setProductDetailDialogOpen] = useState(false);
  const [materialDetailDialogOpen, setMaterialDetailDialogOpen] = useState(false);
  const [selectedRoomStatusGroup, setSelectedRoomStatusGroup] = useState<RoomStatusGroup | null>(null);
  const [selectedProductStatusGroup, setSelectedProductStatusGroup] = useState<ProductStatusGroup | null>(null);
  const [selectedMaterialStatusGroup, setSelectedMaterialStatusGroup] = useState<MaterialStatusGroup | null>(null);
  const [selectedPrintIds, setSelectedPrintIds] = useState<Set<number>>(new Set());
  const [materialDetailSortField, setMaterialDetailSortField] = useState<string>('material');
  const [materialDetailSortDirection, setMaterialDetailSortDirection] = useState<'asc' | 'desc'>('asc');
  const [openBulkStatusDialog, setOpenBulkStatusDialog] = useState(false);
  const [bulkStatusValue, setBulkStatusValue] = useState<string>('');
  const [bulkStatusSaving, setBulkStatusSaving] = useState(false);
  
  // Responsive design detection
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  
  // Edit job modal state
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerSearchInput, setCustomerSearchInput] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState<Customer[]>([]);
  const [openCreateCustomerDialog, setOpenCreateCustomerDialog] = useState(false);
  const [openAddressModal, setOpenAddressModal] = useState(false);
  const [openJobAddressModal, setOpenJobAddressModal] = useState(false);
  const [newCustomerData, setNewCustomerData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    status: 'active' as 'active' | 'inactive',
    createdAt: new Date().toLocaleDateString('en-US'),
  });
  const [newCustomerCompanyName, setNewCustomerCompanyName] = useState('');
  const [newCustomerContactPerson, setNewCustomerContactPerson] = useState('');
  const [formData, setFormData] = useState<Omit<Job, 'id'> & { customerId?: number | null }>({
    job: '',
    customer: '',
    customerAddress: '',
    jobAddress: '',
    apartment: '',
    status: 'Estimate',
    appointmentDate: new Date().toLocaleDateString('en-US'),
    startTime: '09:00 AM',
    endTime: '05:00 PM',
    dueDate: new Date().toLocaleDateString('en-US'),
    customerId: null,
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  
  // Notes and formulas state
  const [notes, setNotes] = useState<string>('');
  const [newNoteEntry, setNewNoteEntry] = useState<string>('');
  const [notesSaving, setNotesSaving] = useState(false);
  const [editingNoteIndex, setEditingNoteIndex] = useState<number | null>(null);
  const [editingNoteText, setEditingNoteText] = useState<string>('');
  const [formulas, setFormulas] = useState<JobNotesFormula[]>([]);
  const [formulasLoading, setFormulasLoading] = useState(false);
  const [openCalculatorDialog, setOpenCalculatorDialog] = useState(false);
  const [calculatorValue, setCalculatorValue] = useState<string>('');
  const [calculatorResult, setCalculatorResult] = useState<number | null>(null);
  const [notesTextareaRef, setNotesTextareaRef] = useState<HTMLTextAreaElement | null>(null);
  const [insertingFormulaId, setInsertingFormulaId] = useState<number | null>(null);

  // Job notification state
  interface JobNotification {
    id: number;
    message: string;
    title: string | null;
    notification_type: string;
    is_read: boolean;
    created_at: string;
    sender_name?: string;
  }
  const [jobNotifications, setJobNotifications] = useState<JobNotification[]>([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const jobUnreadCount = jobNotifications.filter(n => !n.is_read).length;

  const loadJobNotifications = async () => {
    try {
      const response = await apiRequest(`/notifications/job/${jobId}`, { method: 'GET' });
      setJobNotifications(response.data.notifications || []);
    } catch (error) {
      console.error('Error loading job notifications:', error);
    }
  };

  const markJobNotificationRead = async (notifId: number) => {
    try {
      setJobNotifications(prev => prev.map(n => n.id === notifId ? { ...n, is_read: true } : n));
      await apiRequest(`/notifications/${notifId}/read`, { method: 'PUT' });
    } catch (error) {
      console.error('Error marking notification read:', error);
      setJobNotifications(prev => prev.map(n => n.id === notifId ? { ...n, is_read: false } : n));
    }
  };

  const markAllJobNotifsRead = async () => {
    try {
      setJobNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      await apiRequest(`/notifications/job/${jobId}/read-all`, { method: 'PUT' });
    } catch (error) {
      console.error('Error marking all job notifications read:', error);
      loadJobNotifications();
    }
  };

  // Check if user is a Field Worker
  const isFieldWorker = currentUser?.role === 'FIELD AND SHOP TEAM';

  // Detect if device is mobile
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      // Check for touch support and screen width
      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isSmallScreen = window.innerWidth <= 768;
      const isMobileDevice = hasTouch && isSmallScreen;
      setIsMobile(isMobileDevice);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Helper functions for contact actions
  const formatPhoneForCall = (phone: string): string => {
    // Remove all non-numeric characters
    const cleaned = phone.replace(/\D/g, '');
    return cleaned;
  };

  const handlePhoneCall = (phone: string) => {
    const formatted = formatPhoneForCall(phone);
    window.location.href = `tel:${formatted}`;
  };

  const handleWhatsApp = (phone: string) => {
    const whatsappLink = getWhatsAppLink(phone);
    if (whatsappLink) {
      window.open(whatsappLink, '_blank');
    }
  };

  const handleEmail = (email: string) => {
    window.location.href = `mailto:${email}`;
  };

  const handleSMS = (phone: string) => {
    const formatted = formatPhoneForCall(phone);
    window.location.href = `sms:${formatted}`;
  };

  // Handle hash changes for tab navigation
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash === '#chat') setActiveTab(5);
      else if (hash === '#quote') {
        // Redirect field workers away from quote tab
        if (isFieldWorker) {
          setActiveTab(0);
        } else {
          setActiveTab(1);
        }
      }
      else if (hash === '#files') setActiveTab(2);
      else if (hash === '#product') setActiveTab(3);
      else if (hash === '#history') {
        // Redirect field workers away from history tab
        if (isFieldWorker) {
          setActiveTab(0);
        } else {
          setActiveTab(4);
        }
      }
      else if (hash === '#detail' || hash === '') setActiveTab(0);
    };

    // Check hash on mount
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [isFieldWorker]);

  // Redirect field workers away from Quote tab if they somehow access it
  useEffect(() => {
    if (isFieldWorker && activeTab === 1) {
      setActiveTab(0);
    }
  }, [isFieldWorker, activeTab]);

  // Redirect field workers away from History tab if they somehow access it
  useEffect(() => {
    if (isFieldWorker && activeTab === 4) {
      setActiveTab(0);
    }
  }, [isFieldWorker, activeTab]);

  useEffect(() => {
    // Load all data from database every time the component is shown
    // This ensures fresh data every time the page is displayed
    loadJob();
    loadCurrentUser();
    loadCustomers();
    loadStatusLists();
    loadQuotes();
    loadNotesFormulas();
    loadJobNotifications();
  }, [jobId]);

  const loadStatusLists = async () => {
    setStatusListsLoading(true);
    try {
      await Promise.all([loadRooms(), loadProducts(), loadProductTypes(), loadProductDetailStatuses(), loadTypeOfWorkList()]);
    } finally {
      setStatusListsLoading(false);
    }
  };

  const loadRooms = async () => {
    try {
      const response = await apiRequest(`/products/rooms/job/${jobId}`, {
        method: 'GET',
      });
      const data = (response.data || []) as Room[];
      setRooms(data);
    } catch (error) {
      console.error('Failed to load rooms:', error);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await apiRequest(`/products/job/${jobId}`, {
        method: 'GET',
      });
      const data = (response.data || []) as Product[];
      setProducts(data);
    } catch (error) {
      console.error('Failed to load products:', error);
    }
  };

  const loadProductTypes = async () => {
    try {
      const response = await apiRequest('/producttype', { method: 'GET' });
      const data = (response.data || []) as ProductType[];
      setProductTypes(data);
    } catch (error) {
      console.error('Failed to load product types:', error);
    }
  };

  const loadTypeOfWorkList = async () => {
    try {
      const response = await apiRequest('/type-of-work', { method: 'GET' });
      const data = (response.data || []) as Array<{ id: number; workName: string; isActive: boolean }>;
      const activeTypes = data
        .filter((type) => type.isActive)
        .sort((a, b) => a.workName.localeCompare(b.workName))
        .map((type) => type.workName);
      setTypeOfWorkList(activeTypes);
    } catch (error) {
      console.error('Failed to load type of work:', error);
      setTypeOfWorkList([]);
    }
  };

  const loadProductDetailStatuses = async () => {
    try {
      const response = await apiRequest('/product-detail-status', { method: 'GET' });
      const data = (response.data || []) as Array<{ id: number; statusName: string; isActive: boolean }>;
      // Filter only active statuses and sort by status name
      const activeStatuses = data
        .filter((status) => status.isActive)
        .sort((a, b) => a.statusName.localeCompare(b.statusName));
      setProductDetailStatuses(activeStatuses);
    } catch (error) {
      console.error('Failed to load product detail statuses:', error);
      setProductDetailStatuses([]);
    }
  };

  const loadQuotes = async () => {
    try {
      setQuotesLoading(true);
      const response = await apiRequest(`/jobs/${jobId}/quotes`, { method: 'GET' });
      const data = (response.data || []) as any[];
      
      // Map backend response (snake_case) to frontend format (camelCase)
      const mappedQuotes: Quote[] = data.map((q) => ({
        id: q.id,
        jobId: q.job_id,
        name: q.name || '',
        status: q.status || 'Draft',
        pdfUrl: q.pdf_url || null,
        totalAmount: q.total_amount != null ? Number(q.total_amount) : 0,
        createdAt: q.created_at || new Date().toISOString(),
        updatedAt: q.updated_at || new Date().toISOString(),
        items: (q.items || []).map((item: any) => ({
          id: item.id,
          quoteId: item.quote_id,
          description: item.description || '',
          widthInches: item.width_inches != null ? Number(item.width_inches) : null,
          heightInches: item.height_inches != null ? Number(item.height_inches) : null,
          sqFtRate: item.sq_ft_rate != null ? Number(item.sq_ft_rate) : 0,
          lnFtRate: item.ln_ft_rate != null ? Number(item.ln_ft_rate) : 0,
          hardware: item.hardware != null ? Number(item.hardware) : 0,
          quantity: item.quantity != null ? Number(item.quantity) : 1,
          productId: item.product_id != null ? Number(item.product_id) : null,
          hardwareId: item.hardware_id != null ? Number(item.hardware_id) : null,
          discount: item.discount != null ? Number(item.discount) : 0,
          lineTotal: item.line_total != null ? Number(item.line_total) : 0,
          // Preserve original snake_case for direct access if needed
          width_inches: item.width_inches != null ? Number(item.width_inches) : null,
          height_inches: item.height_inches != null ? Number(item.height_inches) : null,
          sq_ft_rate: item.sq_ft_rate != null ? Number(item.sq_ft_rate) : 0,
          ln_ft_rate: item.ln_ft_rate != null ? Number(item.ln_ft_rate) : 0,
          line_total: item.line_total != null ? Number(item.line_total) : 0,
          createdAt: item.created_at || new Date().toISOString(),
          updatedAt: item.updated_at || new Date().toISOString(),
          // Include product and hardware type info for display
          product_type_name: item.product_type_name || null,
          product_type_unit: item.product_type_unit || null,
          hardware_type_name: item.hardware_type_name || null,
          hardware_type_unit: item.hardware_type_unit || null,
        })),
      }));
      
      setQuotes(mappedQuotes);
    } catch (error) {
      console.error('Failed to load quotes:', error);
      setQuotes([]);
    } finally {
      setQuotesLoading(false);
    }
  };

  const loadCurrentUser = async () => {
    try {
      const response = await apiRequest('/users/profile/me', { method: 'GET' });
      setCurrentUser(response.data as User);
    } catch (error) {
      console.error('Failed to load current user:', error);
    }
  };

  const loadJob = async () => {
    try {
      setLoading(true);
      const response = await apiRequest(`/jobs/${jobId}`, { method: 'GET' });
      const j = response.data as any;
      
      // Transform API response to match Job type
      const mappedJob: Job = {
        id: j.id,
        job: j.job_number,
        customer: j.customer_name || '',
        customerAddress: j.customer_address || '',
        jobAddress: j.job_address || '',
        apartment: j.apartment || '',
        status: (j.status || 'Estimate') as Job['status'],
        quoteStatus: j.quote_status as Job['quoteStatus'],
        payStatus: j.pay_status as Job['payStatus'],
        appointmentDate: j.appointment_date
          ? new Date(j.appointment_date).toLocaleDateString('en-US')
          : '',
        startTime: j.start_time || '',
        endTime: j.end_time || '',
        dueDate: j.due_date
          ? new Date(j.due_date).toLocaleDateString('en-US')
          : '',
        userId: j.user_id ?? undefined,
        notes: j.notes || '',
      };
      
      setJob(mappedJob);
      setNotes(j.notes || '');
      setCustomerEmail(j.customer_email || '');
      setCustomerPhone(j.customer_phone || '');
      setCompanyName(j.company_name || '');
      setContactPerson(j.contact_person || '');
    } catch (error) {
      console.error('Error loading job:', error);
      setJob(null);
    } finally {
      setLoading(false);
    }
  };




  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const loadCustomers = async () => {
    try {
      const response = await apiRequest('/customers', { method: 'GET' });
      const data = (response.data || []) as Customer[];
      setCustomers(data);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const searchCustomers = async (searchTerm: string) => {
    try {
      if (!searchTerm.trim()) {
        // If no search term, load all customers
        const response = await apiRequest('/customers', { method: 'GET' });
        const data = (response.data || []) as Customer[];
        setCustomerSearchResults(data);
      } else {
        // Search with term
        const response = await apiRequest(`/customers?search=${encodeURIComponent(searchTerm)}`, { method: 'GET' });
        const data = (response.data || []) as Customer[];
        setCustomerSearchResults(data);
      }
    } catch (error) {
      console.error('Error searching customers:', error);
      setCustomerSearchResults([]);
    }
  };

  const handleEditJob = async () => {
    if (!job) return;
    
    // Check if user is a Field Worker
    const isFieldWorker = currentUser?.role === 'FIELD AND SHOP TEAM';
    if (isFieldWorker) {
      setSnackbar({ open: true, message: 'FIELD AND SHOP TEAM cannot edit jobs' });
      return;
    }
    
    try {
      // Fetch full job details to get customer_id
      const response = await apiRequest(`/jobs/${job.id}`, { method: 'GET' });
      const jobData = response.data as any;
      const customerId = jobData.customer_id || null;
      const selectedCustomer = customerId ? customers.find((c) => c.id === customerId) : null;
      
      setFormData({
        job: job.job,
        customer: job.customer,
        customerAddress: job.customerAddress || '',
        jobAddress: job.jobAddress,
        apartment: job.apartment,
        status: job.status,
        appointmentDate: job.appointmentDate,
        startTime: job.startTime,
        endTime: job.endTime,
        dueDate: job.dueDate,
        customerId,
      });
      
      // Set customer search input for autocomplete
      if (selectedCustomer) {
        setCustomerSearchInput(selectedCustomer.name);
        setCustomerSearchResults([selectedCustomer]);
      } else {
        setCustomerSearchInput(job.customer);
        setCustomerSearchResults([]);
      }
      
      setOpenEditDialog(true);
      // Load all customers when dialog opens
      searchCustomers('');
    } catch (error) {
      console.error('Error loading job details:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load job details',
      });
    }
  };

  const handleCreateCustomer = async () => {
    try {
      if (!newCustomerData.name.trim()) {
        setSnackbar({
          open: true,
          message: 'Customer name is required',
        });
        return;
      }

      if (!newCustomerData.email.trim()) {
        setSnackbar({
          open: true,
          message: 'Customer email is required',
        });
        return;
      }

      // Determine the actual name and contact person
      // If company name exists: name = company name, contact_person = contact person field (or name if not provided)
      // If no company name: name = person's name, contact_person = same as name (default)
      const isCompany = newCustomerCompanyName.trim() !== '';
      const finalName = isCompany ? newCustomerCompanyName.trim() : newCustomerData.name;
      const finalContactPerson = isCompany 
        ? (newCustomerContactPerson.trim() || newCustomerData.name || newCustomerCompanyName.trim()) 
        : (newCustomerContactPerson.trim() || newCustomerData.name);

      const customerData = {
        name: finalName,
        email: newCustomerData.email || null,
        phone: newCustomerData.phone || null,
        address: newCustomerData.address || null,
        company_name: isCompany ? newCustomerCompanyName.trim() : null,
        contact_person: finalContactPerson,
      };

      const response = await api.post('/customers', customerData);
      const newCustomer = response.data as Customer;
      
      // Add to customers list
      setCustomers([...customers, newCustomer]);
      
      // Auto-select the newly created customer
      const jobAddress = newCustomer.address || '';
      
      setFormData({
        ...formData,
        customerId: newCustomer.id,
        customer: newCustomer.name,
        jobAddress,
      });
      
      setCustomerSearchInput(newCustomer.name);
      setCustomerSearchResults([newCustomer]);
      
      // Reload all customers to ensure consistency
      await loadCustomers();
      
      // Close create customer dialog
      setOpenCreateCustomerDialog(false);
      setNewCustomerData({
        name: '',
        email: '',
        phone: '',
        address: '',
        status: 'active',
        createdAt: new Date().toLocaleDateString('en-US'),
      });
      setNewCustomerCompanyName('');
      setNewCustomerContactPerson('');
      
      setSnackbar({ open: true, message: 'Customer created and selected' });
    } catch (error) {
      console.error('Error creating customer:', error);
      const errorMessage = error instanceof Error 
        ? error.message 
        : (error as any)?.response?.data?.message || 'Failed to create customer';
      setSnackbar({
        open: true,
        message: errorMessage,
      });
    }
  };

  const handleSaveJob = async () => {
    if (!job) return;
    
    // Check if user is a Field Worker
    const isFieldWorker = currentUser?.role === 'FIELD AND SHOP TEAM';
    if (isFieldWorker) {
      setSnackbar({ open: true, message: 'FIELD AND SHOP TEAM cannot edit jobs' });
      return;
    }
    
    try {
      const jobData = {
        job_number: formData.job || undefined,
        customer_id: formData.customerId || null,
        job_address: formData.jobAddress,
        apartment: formData.apartment || null,
        status: formData.status,
        appointment_date: formData.appointmentDate
          ? new Date(formData.appointmentDate).toISOString().split('T')[0]
          : null,
        start_time: formData.startTime || null,
        end_time: formData.endTime || null,
        due_date: formData.dueDate
          ? new Date(formData.dueDate).toISOString().split('T')[0]
          : null,
      };

      // Update existing job
      await api.put(`/jobs/${job.id}`, jobData);
      setSnackbar({ open: true, message: 'Job updated successfully' });
      loadJob(); // Reload job from backend
      setOpenEditDialog(false);
    } catch (error) {
      console.error('Error saving job:', error);
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Failed to save job',
      });
    }
  };

  const handleOpenGoogleMaps = () => {
    if (!job) return;
    // Encode the job address for Google Maps URL
    const address = `${job.jobAddress}${job.apartment ? `, ${job.apartment}` : ''}`;
    const encodedAddress = encodeURIComponent(address);
    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
    window.open(googleMapsUrl, '_blank');
  };

  // Group rooms by status and count amount
  const roomStatusGroups = useMemo(() => {
    const groups = new Map<string, Room[]>();
    
    rooms.forEach((room) => {
      const status = room.status || 'active';
      if (!groups.has(status)) {
        groups.set(status, []);
      }
      groups.get(status)!.push(room);
    });

    return Array.from(groups.entries()).map(([status, rooms]) => ({
      status,
      amount: rooms.length,
      rooms,
    })).sort((a, b) => a.status.localeCompare(b.status));
  }, [rooms]);

  // Group products by status and count amount
  const productStatusGroups = useMemo(() => {
    const groups = new Map<string, Product[]>();
    
    products.forEach((product) => {
      const status = product.status || 'N/A';
      if (!groups.has(status)) {
        groups.set(status, []);
      }
      groups.get(status)!.push(product);
    });

    return Array.from(groups.entries()).map(([status, products]) => ({
      status,
      amount: products.length,
      products,
    })).sort((a, b) => a.status.localeCompare(b.status));
  }, [products]);

  // Group product details by status (Material Status List) and count amount
  const materialStatusGroups = useMemo(() => {
    const groups = new Map<string, ProductDetail[]>();
    
    products.forEach((product) => {
      if (!product.details || product.details.length === 0) return;
      
      product.details.forEach((detail) => {
        const status = detail.status || 'N/A';
        if (!groups.has(status)) {
          groups.set(status, []);
        }
        groups.get(status)!.push(detail);
      });
    });

    return Array.from(groups.entries()).map(([status, details]) => ({
      status,
      amount: details.length,
      details,
    })).sort((a, b) => a.status.localeCompare(b.status));
  }, [products]);

  // Keep selectedMaterialStatusGroup in sync when products (and thus materialStatusGroups) update
  useEffect(() => {
    if (selectedMaterialStatusGroup && materialDetailDialogOpen) {
      const updated = materialStatusGroups.find(g => g.status === selectedMaterialStatusGroup.status);
      if (updated) {
        setSelectedMaterialStatusGroup(updated);
      } else {
        // The group no longer exists (all items moved out), close the dialog
        setSelectedMaterialStatusGroup(null);
        setMaterialDetailDialogOpen(false);
      }
    }
  }, [materialStatusGroups]);

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'not active':
        return 'default';
      case 'locked':
        return 'warning';
      case 'finished':
        return 'info';
      default:
        return 'default';
    }
  };

  const handleRoomDetailClick = (statusGroup: RoomStatusGroup) => {
    setSelectedRoomStatusGroup(statusGroup);
    setRoomDetailDialogOpen(true);
  };

  const handleProductDetailClick = (statusGroup: ProductStatusGroup) => {
    setSelectedProductStatusGroup(statusGroup);
    setProductDetailDialogOpen(true);
  };

  const handleMaterialDetailClick = (statusGroup: MaterialStatusGroup) => {
    setSelectedMaterialStatusGroup(statusGroup);
    setSelectedPrintIds(new Set());
    setMaterialDetailDialogOpen(true);
  };

  const handleMaterialDetailSort = (field: string) => {
    setMaterialDetailSortDirection((prev) =>
      materialDetailSortField === field && prev === 'asc' ? 'desc' : 'asc'
    );
    setMaterialDetailSortField(field);
  };

  const getSortedMaterialDetails = (details: ProductDetail[]) => {
    return [...details].sort((a, b) => {
      const dir = materialDetailSortDirection === 'asc' ? 1 : -1;
      switch (materialDetailSortField) {
        case 'material': {
          const prodA = products.find(p => p.id === a.product_id);
          const prodB = products.find(p => p.id === b.product_id);
          const nameA = getProductDetailName(prodA, a);
          const nameB = getProductDetailName(prodB, b);
          return nameA.localeCompare(nameB) * dir;
        }
        case 'status': {
          const sA = a.status || 'None';
          const sB = b.status || 'None';
          return sA.localeCompare(sB) * dir;
        }
        case 'productType': {
          const ptA = productTypes.find(pt => pt.id === a.product_type_id)?.productType || 'Unknown';
          const ptB = productTypes.find(pt => pt.id === b.product_type_id)?.productType || 'Unknown';
          return ptA.localeCompare(ptB) * dir;
        }
        case 'typeOfWork': {
          const twA = a.type_of_work || '';
          const twB = b.type_of_work || '';
          return twA.localeCompare(twB) * dir;
        }
        case 'size': {
          const areaA = (a.width || 0) * (a.height || 0);
          const areaB = (b.width || 0) * (b.height || 0);
          return (areaA - areaB) * dir;
        }
        case 'update': {
          const dateA = a.updated_at || '';
          const dateB = b.updated_at || '';
          return dateA.localeCompare(dateB) * dir;
        }
        default:
          return 0;
      }
    });
  };

  const handleDetailStatusChange = async (productId: number, detailId: number, newStatus: string | null) => {
    // Prevent field workers from changing status
    if (isFieldWorker) {
      setSnackbar({ open: true, message: 'FIELD AND SHOP TEAM cannot change status' });
      return;
    }

    try {
      // If status is empty or null, save as null (which represents 'None' in database)
      const statusToSave = (!newStatus || newStatus.trim() === '') ? null : newStatus;

      await apiRequest(`/products/${productId}/details/${detailId}`, {
        method: 'PUT',
        body: JSON.stringify({
          status: statusToSave,
        }),
      });
      
      // Update local state
      setProducts((prev) => {
        return prev.map((product) => {
          if (product.id === productId && product.details) {
            return {
              ...product,
              details: product.details.map((detail) =>
                detail.id === detailId ? { ...detail, status: statusToSave } : detail
              ),
            };
          }
          return product;
        });
      });
      
      setSnackbar({ open: true, message: 'Status updated successfully' });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to update status',
      });
    }
  };

  const handleDetailTypeOfWorkChange = async (productId: number, detailId: number, newTypeOfWork: string[]) => {
    const typeOfWorkToSave = newTypeOfWork.length > 0 ? newTypeOfWork.join(', ') : null;
    try {
      await apiRequest(`/products/${productId}/details/${detailId}`, {
        method: 'PUT',
        body: JSON.stringify({ type_of_work: typeOfWorkToSave }),
      });

      setProducts((prev) =>
        prev.map((product) => {
          if (product.id === productId && product.details) {
            return {
              ...product,
              details: product.details.map((detail) =>
                detail.id === detailId ? { ...detail, type_of_work: typeOfWorkToSave } : detail
              ),
            };
          }
          return product;
        })
      );

      setSnackbar({ open: true, message: 'Type of work updated successfully' });
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Failed to update type of work' });
    }
  };

  // Status options for product details
  const detailStatusOptions = useMemo(() => {
    // Start with "None" as the first option
    const options = ['None'];
    // Add all active status names from the API (excluding "None" if it exists)
    productDetailStatuses.forEach((status) => {
      if (status.statusName !== 'None') {
        options.push(status.statusName);
      }
    });
    return options;
  }, [productDetailStatuses]);

  // Helper function to generate product detail name
  const getProductDetailName = (product: Product | undefined, detail: ProductDetail): string => {
    if (!product) {
      return `Detail #${detail.number || detail.id}`;
    }

    const parts: string[] = [];
    
    // Add job ID
    if (product.job_id) {
      parts.push(product.job_id.toString());
    }
    
    // Add room if available
    if (product.room) {
      parts.push(product.room);
    }
    
    // Check if it's hardware
    const productType = productTypes.find((pt) => pt.id === detail.product_type_id);
    const isHardware = productType?.unit === 'Hardware';
    if (isHardware) {
      parts.push('HW');
    }
    
    // Add detail number
    if (detail.number) {
      parts.push(detail.number.toString());
    }
    
    // Get total count of details for this product
    const totalCount = product.details?.length || 0;
    
    if (parts.length > 0) {
      return `${parts.join('-')}${totalCount > 0 ? `-${totalCount}` : ''}`;
    }
    
    return `Detail #${detail.number || detail.id}`;
  };

  const handleOpenBulkStatusDialog = () => {
    setBulkStatusValue('');
    setOpenBulkStatusDialog(true);
  };

  const handleCloseBulkStatusDialog = () => {
    setOpenBulkStatusDialog(false);
    setBulkStatusValue('');
  };

  const handleBulkStatusChange = async () => {
    // Prevent field workers from changing status
    if (isFieldWorker) {
      setSnackbar({ open: true, message: 'FIELD AND SHOP TEAM cannot change status' });
      handleCloseBulkStatusDialog();
      return;
    }

    if (!selectedMaterialStatusGroup || !selectedMaterialStatusGroup.details || selectedMaterialStatusGroup.details.length === 0) {
      setSnackbar({ open: true, message: 'No product details found' });
      return;
    }

    // Check if status exists (unless it's "None" or empty)
    let statusToSave: string | null = null;
    if (bulkStatusValue && bulkStatusValue.trim() !== '' && bulkStatusValue !== 'None') {
      const trimmedStatus = bulkStatusValue.trim();
      
      // Check if status exists in options
      if (!detailStatusOptions.includes(trimmedStatus)) {
        setSnackbar({
          open: true,
          message: `Status "${trimmedStatus}" does not exist. Please select an existing status.`,
        });
        return;
      }
      
      statusToSave = trimmedStatus;
    }

    setBulkStatusSaving(true);
    try {
      // Update all details in parallel
      const updatePromises = selectedMaterialStatusGroup.details.map((detail) => {
        const product = products.find(p => p.id === detail.product_id);
        if (!product) return Promise.resolve();
        return apiRequest(`/products/${product.id}/details/${detail.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            status: statusToSave,
          }),
        });
      });

      await Promise.all(updatePromises);

      // Update local state
      setProducts((prev) => {
        return prev.map((product) => {
          if (product.details) {
            const updatedDetails = product.details.map((detail) => {
              // Check if this detail is in the selected group
              const isInGroup = selectedMaterialStatusGroup.details.some(d => d.id === detail.id);
              if (isInGroup) {
                return { ...detail, status: statusToSave };
              }
              return detail;
            });
            return { ...product, details: updatedDetails };
          }
          return product;
        });
      });

      setSnackbar({ open: true, message: `Status updated for all ${selectedMaterialStatusGroup.details.length} product details` });
      handleCloseBulkStatusDialog();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to update statuses',
      });
    } finally {
      setBulkStatusSaving(false);
    }
  };

  const handleDownloadCSV = () => {
    if (!selectedMaterialStatusGroup || !selectedMaterialStatusGroup.details || selectedMaterialStatusGroup.details.length === 0) {
      setSnackbar({ open: true, message: 'No data to export' });
      return;
    }

    // Helper function to escape CSV values
    const escapeCSV = (value: string | number | null | undefined): string => {
      if (value === null || value === undefined) return '';
      const str = String(value);
      // If value contains comma, quote, or newline, wrap in quotes and escape quotes
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    // Create CSV content
    const headers = ['Material', 'Product Type', 'Type of Work', 'Size'];
    const rows = selectedMaterialStatusGroup.details.map((detail) => {
      const product = products.find(p => p.id === detail.product_id);
      const productType = productTypes.find(pt => pt.id === detail.product_type_id);
      const materialName = productType?.productType || 'Unknown';
      const detailName = getProductDetailName(product, detail);
      const typeOfWork = detail.type_of_work || 'N/A';
      const size = formatSize(detail.width, detail.height);

      return [
        escapeCSV(detailName),
        escapeCSV(materialName),
        escapeCSV(typeOfWork),
        escapeCSV(size),
      ];
    });

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(',')),
    ].join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const statusName = selectedMaterialStatusGroup.status || 'all';
    a.download = `product-details-status-${statusName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    setSnackbar({ open: true, message: 'CSV exported successfully' });
  };

  const handlePrintBarcodes = () => {
    if (!selectedMaterialStatusGroup || !selectedMaterialStatusGroup.details || selectedMaterialStatusGroup.details.length === 0) {
      setSnackbar({ open: true, message: 'No product details to print' });
      return;
    }

    const detailsToPrint = selectedPrintIds.size > 0
      ? selectedMaterialStatusGroup.details.filter((d) => selectedPrintIds.has(d.id))
      : selectedMaterialStatusGroup.details;

    const items = detailsToPrint
      .filter((detail) => detail.barcode)
      .map((detail) => {
        const product = products.find(p => p.id === detail.product_id);
        const productType = productTypes.find((pt) => pt.id === detail.product_type_id);
        return {
          barcode: detail.barcode!,
          name: getProductDetailName(product, detail),
          productName: product?.product_name,
          room: product?.room || '',
          address: job?.jobAddress || '',
          productTypeName: productType ? productType.productType : '',
          width: detail.width,
          height: detail.height,
        };
      });

    if (items.length === 0) {
      setSnackbar({ open: true, message: 'No barcodes found to print' });
      return;
    }

    const statusName = selectedMaterialStatusGroup.status || 'All';
    printBarcodes(items, {
      title: `Print Barcodes - Status: ${statusName}`,
      barcodeWidth: 4,
      barcodeHeight: 200,
      displayValue: false,
    });
  };

  const loadNotesFormulas = async () => {
    try {
      setFormulasLoading(true);
      const response = await apiRequest(`/jobs/${jobId}/notes-formulas`, { method: 'GET' });
      setFormulas(response.data as JobNotesFormula[]);
    } catch (error) {
      console.error('Failed to load notes formulas:', error);
      setFormulas([]);
    } finally {
      setFormulasLoading(false);
    }
  };

  const getTimestamp = () => {
    const now = new Date();
    return now.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const parseNoteEntries = (raw: string): string[] => {
    if (!raw.trim()) return [];
    // Split on double newline that precedes a timestamp header [...]
    const entries = raw.split(/\n\n(?=\[)/);
    return entries.filter(e => e.trim());
  };

  const rebuildNotes = (entries: string[]): string => {
    return entries.join('\n\n');
  };

  const saveNotesToServer = async (updatedNotes: string) => {
    if (!job) return;
    await apiRequest(`/jobs/${job.id}`, {
      method: 'PUT',
      body: JSON.stringify({ notes: updatedNotes }),
    });
    loadJob();
  };

  const handleAddNote = async () => {
    if (!job) return;

    const canEdit = currentUser?.role === 'SYSTEM ADMIN' || currentUser?.role === 'SALES REP';
    if (!canEdit) {
      setSnackbar({ open: true, message: 'Only Admin and Office can edit notes' });
      return;
    }

    const trimmedEntry = newNoteEntry.trim();
    if (!trimmedEntry) {
      setSnackbar({ open: true, message: 'Please enter a note before saving' });
      return;
    }

    try {
      setNotesSaving(true);
      const userName = currentUser?.username || 'Unknown';
      const stampedEntry = `[${getTimestamp()} — ${userName}]\n${trimmedEntry}`;
      const updatedNotes = notes ? `${stampedEntry}\n\n${notes}` : stampedEntry;

      await saveNotesToServer(updatedNotes);
      setNewNoteEntry('');
      setSnackbar({ open: true, message: 'Note added successfully' });
    } catch (error) {
      console.error('Error saving notes:', error);
      setSnackbar({ open: true, message: 'Failed to save notes' });
    } finally {
      setNotesSaving(false);
    }
  };

  const handleDeleteNoteEntry = async (index: number) => {
    if (!job) return;
    try {
      setNotesSaving(true);
      const entries = parseNoteEntries(notes);
      entries.splice(index, 1);
      await saveNotesToServer(rebuildNotes(entries));
      setSnackbar({ open: true, message: 'Note deleted' });
    } catch (error) {
      console.error('Error deleting note:', error);
      setSnackbar({ open: true, message: 'Failed to delete note' });
    } finally {
      setNotesSaving(false);
    }
  };

  const handleStartEditNoteEntry = (index: number) => {
    const entries = parseNoteEntries(notes);
    const entry = entries[index];
    // Extract just the body (skip the timestamp header line if present)
    const lines = entry.split('\n');
    const hasHeader = lines[0].startsWith('[');
    const body = hasHeader ? lines.slice(1).join('\n') : entry;
    setEditingNoteIndex(index);
    setEditingNoteText(body);
  };

  const handleSaveNoteEntryEdit = async () => {
    if (!job || editingNoteIndex === null) return;
    const trimmed = editingNoteText.trim();
    if (!trimmed) {
      setSnackbar({ open: true, message: 'Note cannot be empty. Use delete instead.' });
      return;
    }
    try {
      setNotesSaving(true);
      const entries = parseNoteEntries(notes);
      const userName = currentUser?.username || 'Unknown';
      const editStamp = `[Edited: ${getTimestamp()} — ${userName}]`;
      entries[editingNoteIndex] = `${editStamp}\n${trimmed}`;
      await saveNotesToServer(rebuildNotes(entries));
      setEditingNoteIndex(null);
      setEditingNoteText('');
      setSnackbar({ open: true, message: 'Note updated successfully' });
    } catch (error) {
      console.error('Error updating note:', error);
      setSnackbar({ open: true, message: 'Failed to update note' });
    } finally {
      setNotesSaving(false);
    }
  };

  const handleCancelNoteEntryEdit = () => {
    setEditingNoteIndex(null);
    setEditingNoteText('');
  };

  const handleAddFormula = async (label: string, formula: string) => {
    if (!job) return;
    
    try {
      const response = await apiRequest(`/jobs/${job.id}/notes-formulas`, {
        method: 'POST',
        body: JSON.stringify({ label, formula }),
      });
      setFormulas([...formulas, response.data as JobNotesFormula]);
      setSnackbar({ open: true, message: 'Formula added successfully' });
    } catch (error) {
      console.error('Error adding formula:', error);
      setSnackbar({ open: true, message: 'Failed to add formula' });
    }
  };

  const handleUpdateFormula = async (formulaId: number, label: string, formula: string) => {
    if (!job) return;
    
    try {
      // Recalculate result before sending
      calculateFormula(formula);
      const response = await apiRequest(`/jobs/${job.id}/notes-formulas/${formulaId}`, {
        method: 'PUT',
        body: JSON.stringify({ label, formula }),
      });
      setFormulas(formulas.map(f => f.id === formulaId ? response.data as JobNotesFormula : f));
      setSnackbar({ open: true, message: 'Formula updated successfully' });
    } catch (error) {
      console.error('Error updating formula:', error);
      setSnackbar({ open: true, message: 'Failed to update formula' });
    }
  };

  const handleDeleteFormula = async (formulaId: number) => {
    if (!job) return;
    
    try {
      await apiRequest(`/jobs/${job.id}/notes-formulas/${formulaId}`, {
        method: 'DELETE',
      });
      setFormulas(formulas.filter(f => f.id !== formulaId));
      setSnackbar({ open: true, message: 'Formula deleted successfully' });
    } catch (error) {
      console.error('Error deleting formula:', error);
      setSnackbar({ open: true, message: 'Failed to delete formula' });
    }
  };

  const calculateFormula = (formula: string): number | null => {
    try {
      if (!formula.trim()) return null;
      // Sanitize input - only allow numbers, operators, and parentheses
      const sanitized = formula.replace(/[^0-9+\-*/().\s]/g, '');
      if (sanitized !== formula.replace(/\s/g, '')) {
        return null;
      }
      const result = Function(`"use strict"; return (${sanitized})`)();
      if (typeof result === 'number' && isFinite(result)) {
        return result;
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  const handleInsertCalculatorResult = () => {
    if (calculatorResult === null) return;

    const textarea = notesTextareaRef;
    if (!textarea) {
      setNewNoteEntry(prev => prev + (prev ? ' ' : '') + calculatorResult.toString());
    } else {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = newNoteEntry;
      const newText = text.substring(0, start) + calculatorResult.toString() + text.substring(end);
      setNewNoteEntry(newText);
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + calculatorResult.toString().length, start + calculatorResult.toString().length);
      }, 0);
    }
    setOpenCalculatorDialog(false);
    setCalculatorValue('');
    setCalculatorResult(null);
  };

  const handleInsertFormula = async (formula: JobNotesFormula) => {
    if (!job) return;
    
    setInsertingFormulaId(formula.id);
    try {
      // Calculate the formula result
      const calculatedResult = calculateFormula(formula.formula);
      
      // Save/update the formula with the calculated result
      const response = await apiRequest(`/jobs/${job.id}/notes-formulas/${formula.id}`, {
        method: 'PUT',
        body: JSON.stringify({ 
          label: formula.label, 
          formula: formula.formula 
        }),
      });
      
      // Update local state with the saved formula
      const updatedFormula = response.data as JobNotesFormula;
      setFormulas(formulas.map(f => f.id === formula.id ? updatedFormula : f));
      
      // Insert the result into Notes
      const result = updatedFormula.result !== null && updatedFormula.result !== undefined 
        ? Number(updatedFormula.result)
        : calculatedResult;
      
      const text = result !== null && result !== undefined
        ? `${updatedFormula.label}: ${result.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : `${updatedFormula.label}: Invalid formula`;
      
      const textarea = notesTextareaRef;
      if (!textarea) {
        setNewNoteEntry(prev => prev + (prev ? '\n' : '') + text);
      } else {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const currentText = newNoteEntry;
        const newText = currentText.substring(0, start) + text + currentText.substring(end);
        setNewNoteEntry(newText);
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(start + text.length, start + text.length);
        }, 0);
      }

      setSnackbar({ open: true, message: 'Formula calculated, saved, and inserted' });
    } catch (error) {
      console.error('Error calculating and inserting formula:', error);
      setSnackbar({ open: true, message: 'Failed to calculate and insert formula' });
    } finally {
      setInsertingFormulaId(null);
    }
  };

  const handleInsertAllFormulas = () => {
    const allText = formulas
      .map(f => f.result !== null && f.result !== undefined
        ? `${f.label}: ${f.result}`
        : `${f.label}: Invalid formula`)
      .join('\n');

    const textarea = notesTextareaRef;
    if (!textarea) {
      setNewNoteEntry(prev => prev + (prev ? '\n\n' : '') + allText);
    } else {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const currentText = newNoteEntry;
      const newText = currentText.substring(0, start) + '\n' + allText + currentText.substring(end);
      setNewNoteEntry(newText);
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + allText.length + 1, start + allText.length + 1);
      }, 0);
    }
  };


  if (loading) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 2 }}>
          Back to Jobs
        </Button>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (!job) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 2 }}>
          Back to Jobs
        </Button>
        <Typography variant="h6" color="error">
          Job not found
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Button startIcon={<ArrowBackIcon />} onClick={onBack}>
          Back to Jobs
        </Button>
        {currentUser?.role !== 'FIELD AND SHOP TEAM' && (
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={handleEditJob}
            sx={{ bgcolor: 'grey.900', '&:hover': { bgcolor: 'grey.800' } }}
          >
            Edit Job
          </Button>
        )}
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="overline" color="text.secondary">
          Job Details
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            {job.job}
            <Typography component="span" variant="h6" color="text.secondary" sx={{ ml: 1.5, fontWeight: 400 }}>
              #{job.id}
            </Typography>
          </Typography>
          <Chip
            label={job.status}
            color={getStatusColor(job.status) as any}
            size="medium"
          />
          {jobUnreadCount > 0 && (
            <IconButton
              onClick={() => setShowNotifications(!showNotifications)}
              size="small"
              sx={{ ml: 'auto' }}
            >
              <Badge badgeContent={jobUnreadCount} color="error" max={99}>
                <NotificationsIcon />
              </Badge>
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Job Notifications Panel */}
      <Collapse in={showNotifications && jobNotifications.length > 0}>
        <Paper
          elevation={2}
          sx={{
            mb: 3,
            border: '1px solid',
            borderColor: 'warning.light',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1,
              bgcolor: 'warning.light',
              color: 'warning.contrastText',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'grey.900' }}>
              Notifications ({jobUnreadCount} unread)
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {jobUnreadCount > 0 && (
                <Button
                  size="small"
                  startIcon={<MarkChatReadIcon />}
                  onClick={markAllJobNotifsRead}
                  sx={{ color: 'grey.900', textTransform: 'none', fontSize: '0.75rem' }}
                >
                  Mark all read
                </Button>
              )}
              <IconButton size="small" onClick={() => setShowNotifications(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          <List dense sx={{ maxHeight: 250, overflow: 'auto', py: 0 }}>
            {jobNotifications.filter(n => !n.is_read).map((notif) => (
              <ListItem
                key={notif.id}
                sx={{
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'action.hover',
                  '&:last-child': { borderBottom: 'none' },
                }}
                secondaryAction={
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={() => markJobNotificationRead(notif.id)}
                    title="Dismiss"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={notif.message}
                  secondary={
                    `${notif.sender_name || 'System'} - ${new Date(notif.created_at).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`
                  }
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      </Collapse>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={(() => {
            // Calculate visible tab index based on hidden tabs
            if (isFieldWorker) {
              // For field workers: Detail(0), Files(2), Product(3), Chat(5)
              if (activeTab === 0) return 0;
              if (activeTab === 2) return 1; // Files
              if (activeTab === 3) return 2; // Product
              if (activeTab === 5) return 3; // Chat
              return 0; // Default
            } else {
              // For non-field workers: all tabs visible
              return activeTab;
            }
          })()}
          onChange={(event, newValue) => {
            // Map visible tab index to actual activeTab
            if (isFieldWorker) {
              // For field workers: 0=Detail, 1=Files, 2=Product, 3=Chat
              const tabMap = [0, 2, 3, 5];
              handleTabChange(event, tabMap[newValue] || 0);
            } else {
              // For non-field workers: direct mapping
              handleTabChange(event, newValue);
            }
          }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab label="Detail" />
          {!isFieldWorker && <Tab label="Quote" />}
          <Tab label="Files" />
          <Tab label="Product" />
          {!isFieldWorker && <Tab label="History" />}
          <Tab label="Chat" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 3,
          }}
        >
          {/* Left Side */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Job Location */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Job Location
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Job Address
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 0.5 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" component="span">
                          {job.jobAddress}
                        </Typography>
                        {job.apartment && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                            {job.apartment.split(',').filter(a => a.trim()).map((apt, idx) => (
                              <Chip key={idx} label={apt.trim()} size="small" />
                            ))}
                          </Box>
                        )}
                      </Box>
                      <IconButton
                        size="small"
                        onClick={handleOpenGoogleMaps}
                        title="Open in Google Maps"
                        color="primary"
                      >
                        <LocationOnIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Customer Information */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Customer Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {companyName ? 'Company Name' : 'Customer Name'}
                    </Typography>
                    <Typography variant="body1">{companyName || job.customer}</Typography>
                  </Box>
                  {companyName && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Contact Person
                      </Typography>
                      <Typography variant="body1">{contactPerson || job.customer}</Typography>
                    </Box>
                  )}
                  {customerEmail && (
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          Email
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Send Email">
                            <IconButton
                              size="small"
                              onClick={() => handleEmail(customerEmail)}
                              color="primary"
                              sx={{ p: 0.5 }}
                            >
                              <EmailIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                      <Typography variant="body1">{customerEmail}</Typography>
                    </Box>
                  )}
                  {customerPhone && (
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          Phone
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title={isMobile ? "Call" : "Phone calls are only available on mobile devices"}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handlePhoneCall(customerPhone)}
                                color="primary"
                                disabled={!isMobile}
                                sx={{ p: 0.5 }}
                              >
                                <PhoneIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="WhatsApp">
                            <IconButton
                              size="small"
                              onClick={() => handleWhatsApp(customerPhone)}
                              color="success"
                              sx={{ p: 0.5 }}
                            >
                              <WhatsAppIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={isMobile ? "Send SMS" : "SMS is only available on mobile devices"}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleSMS(customerPhone)}
                                color="primary"
                                disabled={!isMobile}
                                sx={{ p: 0.5 }}
                              >
                                <MessageIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </Box>
                      <Typography variant="body1">{customerPhone}</Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Customer Address
                    </Typography>
                    <Typography variant="body1">{job.customerAddress}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Notes */}
            {(currentUser?.role === 'SYSTEM ADMIN' || currentUser?.role === 'SALES REP') && (
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Notes
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setOpenCalculatorDialog(true)}
                      title="Open Calculator"
                      color="primary"
                    >
                      <CalculateIcon />
                    </IconButton>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  {/* Notes entries */}
                  {parseNoteEntries(notes).map((entry, index) => {
                    const lines = entry.split('\n');
                    const hasHeader = lines[0].startsWith('[');
                    const header = hasHeader ? lines[0] : null;
                    const body = hasHeader ? lines.slice(1).join('\n') : entry;

                    return (
                      <Box
                        key={index}
                        sx={{
                          mb: 1.5,
                          p: 1.5,
                          bgcolor: 'action.hover',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        {editingNoteIndex === index ? (
                          <Box>
                            <TextField
                              multiline
                              rows={3}
                              fullWidth
                              value={editingNoteText}
                              onChange={(e) => setEditingNoteText(e.target.value)}
                              disabled={notesSaving}
                              variant="outlined"
                              size="small"
                              sx={{ mb: 1 }}
                            />
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                variant="contained"
                                onClick={handleSaveNoteEntryEdit}
                                disabled={notesSaving}
                                size="small"
                                startIcon={<SaveIcon />}
                              >
                                {notesSaving ? 'Saving...' : 'Save'}
                              </Button>
                              <Button
                                variant="outlined"
                                onClick={handleCancelNoteEntryEdit}
                                disabled={notesSaving}
                                size="small"
                                startIcon={<CloseIcon />}
                              >
                                Cancel
                              </Button>
                            </Box>
                          </Box>
                        ) : (
                          <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                {header && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                    {header}
                                  </Typography>
                                )}
                                <Typography
                                  variant="body2"
                                  sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                                >
                                  {body}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', ml: 1, flexShrink: 0 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleStartEditNoteEntry(index)}
                                  title="Edit note"
                                  disabled={notesSaving}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteNoteEntry(index)}
                                  title="Delete note"
                                  color="error"
                                  disabled={notesSaving}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                  {/* Add new note */}
                  <Box sx={{ mt: 2 }}>
                    <TextField
                      inputRef={(el) => setNotesTextareaRef(el)}
                      multiline
                      rows={3}
                      fullWidth
                      value={newNoteEntry}
                      onChange={(e) => setNewNoteEntry(e.target.value)}
                      disabled={notesSaving}
                      placeholder="Add a new note..."
                      variant="outlined"
                      sx={{ mb: 1 }}
                    />
                    <Button
                      variant="outlined"
                      onClick={handleAddNote}
                      disabled={notesSaving}
                      size="small"
                      startIcon={<AddIcon />}
                    >
                      {notesSaving && editingNoteIndex === null ? 'Saving...' : 'Add Note'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Notes - Read-only for Field Workers */}
            {currentUser?.role === 'FIELD AND SHOP TEAM' && (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Notes
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography
                    variant="body1"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      minHeight: '150px',
                      p: 2,
                      bgcolor: 'action.hover',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {notes || 'No notes available.'}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Notes Table */}
            {(currentUser?.role === 'SYSTEM ADMIN' || currentUser?.role === 'SALES REP') && (
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Notes Table
                    </Typography>
                    {formulas.length > 0 && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleInsertAllFormulas}
                      >
                        Insert All
                      </Button>
                    )}
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  {formulasLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Label</TableCell>
                            <TableCell>Formula</TableCell>
                            <TableCell align="right">Result</TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {formulas.map((formula) => (
                            <TableRow key={formula.id}>
                              <TableCell>
                                <TextField
                                  size="small"
                                  value={formula.label}
                                  onChange={(e) => {
                                    handleUpdateFormula(formula.id, e.target.value, formula.formula);
                                  }}
                                  fullWidth
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  size="small"
                                  value={formula.formula}
                                  onChange={(e) => {
                                    handleUpdateFormula(formula.id, formula.label, e.target.value);
                                  }}
                                  fullWidth
                                />
                              </TableCell>
                              <TableCell align="right">
                                {formula.result !== null && formula.result !== undefined ? (
                                  <Typography variant="body2" fontWeight="medium">
                                    {Number(formula.result).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </Typography>
                                ) : (
                                  <Typography variant="body2" color="error">
                                    Invalid formula
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleInsertFormula(formula)}
                                    disabled={insertingFormulaId === formula.id}
                                  >
                                    {insertingFormulaId === formula.id ? 'Calculating...' : 'Insert'}
                                  </Button>
                                  {currentUser?.role === 'SYSTEM ADMIN' && (
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteFormula(formula.id)}
                                      color="error"
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                          <TableRow>
                            <TableCell colSpan={4}>
                              <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() => {
                                  const newLabel = `Formula ${formulas.length + 1}`;
                                  const newFormula = '0';
                                  handleAddFormula(newLabel, newFormula);
                                }}
                                fullWidth
                                variant="outlined"
                              >
                                Add Row
                              </Button>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Job Information */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Job Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Job Name
                    </Typography>
                    <Typography variant="body1">
                      {job.job}
                      <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        #{job.id}
                      </Typography>
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Status
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={job.status}
                        color={getStatusColor(job.status) as any}
                        size="small"
                      />
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Appointment Date
                    </Typography>
                    <Typography variant="body1">
                      {formatDateForDisplay(job.appointmentDate)} {job.startTime} - {job.endTime}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Due Date
                    </Typography>
                    <Typography variant="body1"> {formatDateForDisplay(job.dueDate)}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Right Side */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Quote Content */}
            {!isFieldWorker && (
            <Paper>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
                <Typography variant="h6">Quote</Typography>
                <IconButton onClick={loadQuotes} disabled={quotesLoading} size="small">
                  <RefreshIcon />
                </IconButton>
              </Box>
              {quotesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : quotes.length === 0 ? (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary" align="center">
                    No quotes found
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ p: 2 }}>
                  {quotes.map((quote) => {
                    // Recalculate total from items to ensure accuracy
                    const calculatedTotal = quote.items?.reduce((sum, item) => {
                      return sum + (item.lineTotal || 0);
                    }, 0) || 0;
                    const displayTotal = calculatedTotal > 0 ? calculatedTotal : (quote.totalAmount || 0);
                    
                    return (
                    <Box key={quote.id} sx={{ mb: 2, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="medium">
                            {quote.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Status: {quote.status || 'Draft'}
                          </Typography>
                        </Box>
                        <Chip
                          label={quote.status || 'Draft'}
                          size="small"
                          color={
                            quote.status === 'Approved' ? 'success' :
                            quote.status === 'Sent' ? 'info' :
                            quote.status === 'Rejected' ? 'error' :
                            'default'
                          }
                        />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {quote.items?.length || 0} item(s)
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          Total: ${displayTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                      </Box>
                      
                      {/* Quote Items Details */}
                      {quote.items && quote.items.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 'medium' }}>
                            Items:
                          </Typography>
                          {quote.items.map((item: any) => {
                            // Access mapped camelCase properties
                            const width = item.widthInches != null ? Number(item.widthInches) : null;
                            const height = item.heightInches != null ? Number(item.heightInches) : null;
                            // Calculate square foot: (width * height) / 144, but handle null/0 cases
                            const squareFoot = (width != null && width > 0 && height != null && height > 0) 
                              ? ((width * height) / 144) 
                              : 0;
                            // Calculate linear foot: width / 12, but handle null/0 cases
                            const linearFoot = (width != null && width > 0) ? (width / 12) : 0;
                            // Use camelCase properties from mapped items
                            const sqFtRate = item.sqFtRate != null ? Number(item.sqFtRate) : 0;
                            const lnFtRate = item.lnFtRate != null ? Number(item.lnFtRate) : 0;
                            const hardware = item.hardware != null ? Number(item.hardware) : 0;
                            const quantity = item.quantity != null ? Number(item.quantity) : 1;
                            const discount = item.discount != null ? Number(item.discount) : 0;
                            
                            const productTypeName = item.product_type_name || null;
                            const productTypeUnit = item.product_type_unit || null;
                            const hardwareTypeName = item.hardware_type_name || null;
                            const hardwareTypeUnit = item.hardware_type_unit || null;
                            
                            // Calculate line total using the same formula as backend
                            // rate = squareFoot * sqFtRate + linearFoot * lnFtRate + hardware
                            const rate = squareFoot * sqFtRate + linearFoot * lnFtRate + hardware;
                            // baseAmount = rate * quantity
                            const baseAmount = rate * quantity;
                            // discountMultiplier = 1 - (discount / 100)
                            const discountMultiplier = 1 - (discount / 100);
                            // lineTotal = baseAmount * discountMultiplier
                            const calculatedLineTotal = baseAmount * discountMultiplier;
                            // Use calculated line total (should match backend, but recalculate to ensure accuracy)
                            const lineTotal = calculatedLineTotal;
                            
                            // Build tooltip content with all details
                            const tooltipContent = (
                              <Box sx={{ p: 1 }}>
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                  {item.description || 'No description'}
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  {productTypeName && (
                                    <Typography variant="body2">
                                      <strong>Product:</strong> {productTypeName}{productTypeUnit ? ` (${productTypeUnit})` : ''}
                                    </Typography>
                                  )}
                                  {hardwareTypeName && (
                                    <Typography variant="body2">
                                      <strong>Hardware:</strong> {hardwareTypeName}{hardwareTypeUnit ? ` (${hardwareTypeUnit})` : ''}
                                    </Typography>
                                  )}
                                  {width != null && (
                                    <Typography variant="body2">
                                      <strong>Width:</strong> {width}"
                                    </Typography>
                                  )}
                                  {height != null && (
                                    <Typography variant="body2">
                                      <strong>Height:</strong> {height}"
                                    </Typography>
                                  )}
                                  <Typography variant="body2">
                                    <strong>Square Foot:</strong> {squareFoot.toFixed(2)}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Linear Foot:</strong> {linearFoot.toFixed(2)}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Sq Ft Rate:</strong> ${sqFtRate.toFixed(2)}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Ln Ft Rate:</strong> ${lnFtRate.toFixed(2)}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Hardware:</strong> ${hardware.toFixed(2)}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Quantity:</strong> {quantity}
                                  </Typography>
                                  {discount > 0 && (
                                    <Typography variant="body2">
                                      <strong>Discount:</strong> {discount.toFixed(2)}%
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            );
                            
                            return (
                              <Tooltip
                                key={item.id}
                                title={tooltipContent}
                                arrow
                                placement="top"
                                componentsProps={{
                                  tooltip: {
                                    sx: {
                                      bgcolor: 'rgba(0, 0, 0, 0.9)',
                                      maxWidth: 400,
                                      fontSize: '0.875rem',
                                    },
                                  },
                                }}
                              >
                                <Box 
                                  sx={{ 
                                    mb: 1.5, 
                                    p: 1.5, 
                                    // backgroundColor: 'grey.50', 
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    cursor: 'pointer',
                                    '&:hover': {
                                      // backgroundColor: 'grey.100',
                                      borderColor: 'primary.main',
                                    },
                                    transition: 'all 0.2s',
                                  }}
                                >
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                    <Box sx={{ flex: 1 }}>
                                      <Typography variant="body2" fontWeight="medium" sx={{ mb: 0.5 }}>
                                        {item.description || 'No description'}
                                      </Typography>
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
                                        {productTypeName && (
                                          <Chip 
                                            label={`Product: ${productTypeName}${productTypeUnit ? ` (${productTypeUnit})` : ''}`}
                                            size="small" 
                                            variant="outlined"
                                            color="primary"
                                            sx={{ height: 22, fontSize: '0.7rem' }}
                                          />
                                        )}
                                        {hardwareTypeName && (
                                          <Chip 
                                            label={`Hardware: ${hardwareTypeName}${hardwareTypeUnit ? ` (${hardwareTypeUnit})` : ''}`}
                                            size="small" 
                                            variant="outlined"
                                            color="secondary"
                                            sx={{ height: 22, fontSize: '0.7rem' }}
                                          />
                                        )}
                                      </Box>
                                    </Box>
                                    <Typography variant="body2" fontWeight="bold" color="primary.main">
                                      ${lineTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </Typography>
                                  </Box>
                                  
                                  
                                </Box>
                              </Tooltip>
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                    );
                  })}
                  {quotes.length > 0 && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: '2px solid', borderColor: 'primary.main' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          Grand Total
                        </Typography>
                        <Typography variant="h6" fontWeight="bold" color="primary.main">
                          ${quotes.reduce((sum, quote) => {
                            // Recalculate total from items for each quote
                            const calculatedTotal = quote.items?.reduce((itemSum, item) => {
                              return itemSum + (item.lineTotal || 0);
                            }, 0) || 0;
                            return sum + (calculatedTotal > 0 ? calculatedTotal : (quote.totalAmount || 0));
                          }, 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Paper>
            )}

            {/* Room Status List */}
            <Paper>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
                <Typography variant="h6">Room Status List</Typography>
                <IconButton onClick={loadStatusLists} disabled={statusListsLoading} size="small">
                  <RefreshIcon />
                </IconButton>
              </Box>
              {statusListsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Detail</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {roomStatusGroups.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} align="center">
                            No rooms found
                          </TableCell>
                        </TableRow>
                      ) : (
                        roomStatusGroups.map((group) => (
                          <TableRow key={group.status} hover>
                            <TableCell>
                              <Chip
                                label={group.status}
                                color={getStatusColor(group.status) as any}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight="medium">
                                {group.amount}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleRoomDetailClick(group)}
                              >
                                Detail
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>

            {/* Product Status List */}
            <Paper>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
                <Typography variant="h6">Product Status List</Typography>
                <IconButton onClick={loadStatusLists} disabled={statusListsLoading} size="small">
                  <RefreshIcon />
                </IconButton>
              </Box>
              {statusListsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Detail</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {productStatusGroups.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} align="center">
                            No products found
                          </TableCell>
                        </TableRow>
                      ) : (
                        productStatusGroups.map((group) => (
                          <TableRow key={group.status} hover>
                            <TableCell>
                              <Chip
                                label={group.status}
                                color={getStatusColor(group.status) as any}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight="medium">
                                {group.amount}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleProductDetailClick(group)}
                              >
                                Detail
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>

            {/* Material Status List */}
            <Paper>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
                <Typography variant="h6">Material Status List</Typography>
                <IconButton onClick={loadStatusLists} disabled={statusListsLoading} size="small">
                  <RefreshIcon />
                </IconButton>
              </Box>
              {statusListsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Detail</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {materialStatusGroups.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} align="center">
                            No materials found
                          </TableCell>
                        </TableRow>
                      ) : (
                        materialStatusGroups.map((group) => (
                          <TableRow key={group.status} hover>
                            <TableCell>
                              <Chip
                                label={group.status}
                                color={getStatusColor(group.status) as any}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight="medium">
                                {group.amount}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleMaterialDetailClick(group)}
                              >
                                Detail
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Box>
        </Box>
      )}

      {activeTab === 1 && !isFieldWorker && (
        <QuoteComponent
          jobId={jobId}
          getJobById={getJobById}
          asTab={true}
          isActive={activeTab === 1}
        />
      )}

      {activeTab === 2 && (
        <Files
          jobId={jobId}
          asTab={true}
          isActive={activeTab === 2}
        />
      )}

      {activeTab === 3 && (
        <Product
          jobId={jobId}
          asTab={true}
        />
      )}

      {activeTab === 4 && !isFieldWorker && (
        <History
          jobId={jobId}
          asTab={true}
          isActive={activeTab === 4}
        />
      )}

      {activeTab === 5 && currentUser && (
        <Box sx={{ mt: 2 }}>
          <ChatPanel jobId={jobId} currentUserId={currentUser.id} />
        </Box>
      )}

      {activeTab === 5 && !currentUser && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Edit Job Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Job</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Autocomplete
              options={customerSearchResults}
              inputValue={customerSearchInput}
              onInputChange={(_event, newInputValue) => {
                setCustomerSearchInput(newInputValue);
                // Search customers (empty string will load all)
                searchCustomers(newInputValue);
              }}
              value={customerSearchResults.find((c) => c.id === formData.customerId) || customers.find((c) => c.id === formData.customerId) || null}
              onChange={(_event, newValue) => {
                if (newValue) {
                  const selectedCustomer = newValue as Customer;
                  
                  // Check if "Create New Customer" option was selected
                  if (selectedCustomer.id === -1) {
                    setNewCustomerData({
                      name: customerSearchInput,
                      email: '',
                      phone: '',
                      address: '',
                      status: 'active',
                      createdAt: new Date().toLocaleDateString('en-US'),
                    });
                    setNewCustomerCompanyName('');
                    setNewCustomerContactPerson('');
                    setOpenCreateCustomerDialog(true);
                    // Don't update formData yet, wait for customer creation
                    return;
                  }
                  
                  // Regular customer selection
                  const jobAddress = selectedCustomer.address || '';
                  setFormData({
                    ...formData,
                    customerId: selectedCustomer.id,
                    customer: selectedCustomer.name,
                    jobAddress,
                  });
                } else {
                  setFormData({
                    ...formData,
                    customerId: null,
                    customer: '',
                    jobAddress: '',
                  });
                }
              }}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return `${option.name}${option.phone ? ` - ${option.phone}` : ''}`;
              }}
              filterOptions={(options, params) => {
                // Show "Create New Customer" option if no results and user has typed something
                const filtered = [...options];
                const { inputValue } = params;
                
                if (inputValue && inputValue.trim() !== '') {
                  const isExisting = options.some(
                    (option) =>
                      option.name.toLowerCase() === inputValue.toLowerCase() ||
                      option.phone === inputValue
                  );
                  
                  if (!isExisting) {
                    filtered.push({
                      id: -1, // Special ID for "create new" option
                      name: inputValue,
                      email: '',
                      phone: '',
                      address: '',
                      city: '',
                      state: '',
                      zip_code: '',
                      country: '',
                    } as Customer);
                  }
                }
                
                return filtered;
              }}
              renderOption={(props, option) => {
                if (option.id === -1) {
                  return (
                    <li {...props} key="create-new">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
                        <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
                          + Create "{option.name}"
                        </Typography>
                      </Box>
                    </li>
                  );
                }
                return (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body1">{option.name}</Typography>
                      {option.phone && (
                        <Typography variant="caption" color="text.secondary">
                          {option.phone}
                        </Typography>
                      )}
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Customer (Name or Phone)"
                  placeholder="Type to search..."
                  required
                />
              )}
              freeSolo={false}
              loading={false}
            />
            <TextField
              label="Job Address"
              value={formData.jobAddress}
              onClick={() => setOpenJobAddressModal(true)}
              onChange={(e) =>
                setFormData({ ...formData, jobAddress: e.target.value })
              }
              placeholder="Click to select address on map..."
              fullWidth
              required
              multiline
              rows={2}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setOpenJobAddressModal(true)}
                      edge="end"
                      title="Open map to select address"
                    >
                      <MapIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={formData.apartment ? formData.apartment.split(',').map(a => a.trim()).filter(a => a) : []}
              onChange={(_, newValue) => {
                const apartments = newValue.map(v => typeof v === 'string' ? v.trim() : v).filter(v => v);
                setFormData({ 
                  ...formData, 
                  apartment: apartments.join(',') 
                });
              }}
              onInputChange={(_, newInputValue, reason) => {
                // Handle comma-separated input when user types comma
                if (reason === 'input' && newInputValue.endsWith(',')) {
                  const valueBeforeComma = newInputValue.slice(0, -1).trim();
                  if (valueBeforeComma) {
                    const currentApartments = formData.apartment ? formData.apartment.split(',').map(a => a.trim()).filter(a => a) : [];
                    if (!currentApartments.includes(valueBeforeComma)) {
                      setFormData({ 
                        ...formData, 
                        apartment: [...currentApartments, valueBeforeComma].join(',') 
                      });
                    }
                    // Return empty to clear input - but this won't work directly
                    // We'll handle it via the input's onKeyDown
                  }
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Apartment"
                  placeholder="Type apartment and press Enter or comma to add"
                  onKeyDown={(e) => {
                    // Handle comma key
                    if (e.key === ',') {
                      e.preventDefault();
                      const input = e.currentTarget.querySelector('input') as HTMLInputElement;
                      if (input && input.value) {
                        const value = input.value.trim();
                        if (value) {
                          const currentApartments = formData.apartment ? formData.apartment.split(',').map(a => a.trim()).filter(a => a) : [];
                          if (!currentApartments.includes(value)) {
                            setFormData({ 
                              ...formData, 
                              apartment: [...currentApartments, value].join(',') 
                            });
                          }
                          // Clear the input
                          input.value = '';
                          // Trigger input change to update Autocomplete's internal state
                          const inputEvent = new Event('input', { bubbles: true });
                          input.dispatchEvent(inputEvent);
                        }
                      }
                    }
                  }}
                  fullWidth
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option}
                    {...getTagProps({ index })}
                    key={index}
                    onDelete={() => {
                      const currentApartments = formData.apartment ? formData.apartment.split(',').map(a => a.trim()).filter(a => a) : [];
                      const newApartments = currentApartments.filter((_, i) => i !== index);
                      setFormData({ 
                        ...formData, 
                        apartment: newApartments.join(',') 
                      });
                    }}
                  />
                ))
              }
            />
            <TextField
              select
              label="Status"
              value={formData.status}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  status: e.target.value as Job['status'],
                })
              }
              fullWidth
            >
              {statuses.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="Appointment Date"
                type="date"
                value={formatDateForInput(formData.appointmentDate)}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    appointmentDate: formatDateForDisplay(e.target.value),
                  })
                }
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <TextField
                label="Due Date"
                type="date"
                value={formatDateForInput(formData.dueDate)}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    dueDate: formatDateForDisplay(e.target.value),
                  })
                }
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="Start Time"
                type="time"
                value={convertTo24Hour(formData.startTime)}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    startTime: convertTo12Hour(e.target.value),
                  })
                }
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <TextField
                label="End Time"
                type="time"
                value={convertTo24Hour(formData.endTime)}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    endTime: convertTo12Hour(e.target.value),
                  })
                }
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSaveJob}
            variant="contained"
            disabled={!formData.customerId || !formData.jobAddress}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Customer Dialog */}
      <Dialog
        open={openCreateCustomerDialog}
        onClose={() => setOpenCreateCustomerDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Customer</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Company Name (Optional)"
              value={newCustomerCompanyName}
              onChange={(e) => {
                setNewCustomerCompanyName(e.target.value);
                if (!e.target.value.trim()) {
                  setNewCustomerContactPerson('');
                }
              }}
              fullWidth
              placeholder="Leave empty if customer is an individual"
              helperText="Leave empty if customer is not a company"
              autoFocus
            />
            <TextField
              label="Contact Person"
              value={newCustomerContactPerson}
              onChange={(e) => setNewCustomerContactPerson(e.target.value)}
              fullWidth
              placeholder={newCustomerCompanyName.trim() ? "Enter contact person name" : newCustomerData.name || "Same as name"}
              helperText={newCustomerCompanyName.trim() ? "Contact person (different from company name)" : "Defaults to name if not specified"}
            />
            <TextField
              label={newCustomerCompanyName.trim() ? "Contact Person Name" : "Customer Name"}
              value={newCustomerData.name}
              onChange={(e) =>
                setNewCustomerData({ ...newCustomerData, name: e.target.value })
              }
              fullWidth
              required
              helperText={newCustomerCompanyName.trim() ? "Name of the contact person at the company" : "Customer name (person's name)"}
            />
            <TextField
              label="Email"
              type="text"
              value={newCustomerData.email}
              onChange={(e) =>
                setNewCustomerData({ ...newCustomerData, email: e.target.value })
              }
              fullWidth
              required
              placeholder="email1@example.com, email2@example.com"
              helperText="Multiple emails can be separated by comma"
            />
            <TextField
              label="Phone"
              value={newCustomerData.phone}
              onChange={(e) => {
                const formatted = formatPhoneInput(e.target.value);
                setNewCustomerData({ ...newCustomerData, phone: formatted });
              }}
              fullWidth
              placeholder="(555) 123-4567"
              inputProps={{
                maxLength: 14, // (555) 123-4567
              }}
            />
            <TextField
              label="Address"
              value={newCustomerData.address || ''}
              onClick={() => setOpenAddressModal(true)}
              onChange={(e) =>
                setNewCustomerData({ ...newCustomerData, address: e.target.value })
              }
              placeholder="Click to select address on map..."
              multiline
              rows={2}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setOpenAddressModal(true)}
                      edge="end"
                      title="Open map to select address"
                    >
                      <MapIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              label="Status"
              value={newCustomerData.status}
              onChange={(e) =>
                setNewCustomerData({
                  ...newCustomerData,
                  status: e.target.value as 'active' | 'inactive',
                })
              }
              fullWidth
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
            <TextField
              label="Created At"
              value={newCustomerData.createdAt}
              onChange={(e) =>
                setNewCustomerData({ ...newCustomerData, createdAt: e.target.value })
              }
              fullWidth
              placeholder="MM/DD/YYYY"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateCustomerDialog(false)}>Cancel</Button>
          <Button
            onClick={handleCreateCustomer}
            variant="contained"
            disabled={!newCustomerData.name.trim() || !newCustomerData.email.trim()}
          >
            Create & Select
          </Button>
        </DialogActions>
      </Dialog>

      {/* Address Map Modal - Using Google Maps with American standard address format */}
      <AddressMapModal
        open={openAddressModal}
        onClose={() => setOpenAddressModal(false)}
        onConfirm={(address) => {
          setNewCustomerData({ ...newCustomerData, address });
          setOpenAddressModal(false);
        }}
        initialAddress={newCustomerData.address}
      />

      {/* Job Address Map Modal - Using Google Maps with American standard address format */}
      <AddressMapModal
        open={openJobAddressModal}
        onClose={() => setOpenJobAddressModal(false)}
        onConfirm={(address) => {
          setFormData({ ...formData, jobAddress: address });
          setOpenJobAddressModal(false);
        }}
        initialAddress={formData.jobAddress}
      />

      {/* Calculator Dialog */}
      <Dialog
        open={openCalculatorDialog}
        onClose={() => {
          setOpenCalculatorDialog(false);
          setCalculatorValue('');
          setCalculatorResult(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Calculator</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Enter Formula"
              value={calculatorValue}
              onChange={(e) => {
                const value = e.target.value;
                setCalculatorValue(value);
                // Auto-calculate on change
                const result = calculateFormula(value);
                setCalculatorResult(result);
              }}
              placeholder="e.g., 12*3, 1000/2, (24+36)/2"
              fullWidth
              autoFocus
              helperText="Supports: + - * / ( )"
            />
            {calculatorResult !== null ? (
              <Alert severity="success">
                Result: <strong>{calculatorResult.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
              </Alert>
            ) : calculatorValue.trim() ? (
              <Alert severity="error">Invalid formula</Alert>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenCalculatorDialog(false);
              setCalculatorValue('');
              setCalculatorResult(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleInsertCalculatorResult}
            variant="contained"
            disabled={calculatorResult === null}
          >
            Insert Result
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity="success"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Room Detail Dialog */}
      <Dialog
        open={roomDetailDialogOpen}
        onClose={() => setRoomDetailDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Rooms with Status: {selectedRoomStatusGroup?.status}
        </DialogTitle>
        <DialogContent>
          {selectedRoomStatusGroup && (
            <List>
              {selectedRoomStatusGroup.rooms.map((room) => (
                <ListItem key={room.id}>
                  <ListItemText
                    primary={room.room_name}
                    secondary={`Created: ${new Date(room.created_at).toLocaleDateString()}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoomDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Product Detail Dialog */}
      <Dialog
        open={productDetailDialogOpen}
        onClose={() => setProductDetailDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Products with Status: {selectedProductStatusGroup?.status}
        </DialogTitle>
        <DialogContent>
          {selectedProductStatusGroup && (
            <List>
              {selectedProductStatusGroup.products.map((product) => (
                <ListItem key={product.id}>
                  <ListItemText
                    primary={product.product_name}
                    secondary={`Room: ${product.room || 'N/A'} | Created: ${product.created_at ? new Date(product.created_at).toLocaleDateString() : 'N/A'}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProductDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Material Detail Dialog */}
      <Dialog
        open={materialDetailDialogOpen}
        onClose={() => setMaterialDetailDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={isSmallScreen}
      >
        <DialogTitle>
          Product Details with Status: {selectedMaterialStatusGroup?.status}
        </DialogTitle>
        <DialogContent>
          {selectedMaterialStatusGroup && (
            <>
              {/* Desktop Table View */}
              {!isSmallScreen && (
                <TableContainer component={Paper} sx={{ mt: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            indeterminate={
                              selectedPrintIds.size > 0 &&
                              selectedPrintIds.size < selectedMaterialStatusGroup.details.length
                            }
                            checked={
                              selectedMaterialStatusGroup.details.length > 0 &&
                              selectedPrintIds.size === selectedMaterialStatusGroup.details.length
                            }
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedPrintIds(new Set(selectedMaterialStatusGroup.details.map((d) => d.id)));
                              } else {
                                setSelectedPrintIds(new Set());
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell sortDirection={materialDetailSortField === 'material' ? materialDetailSortDirection : false}>
                          <TableSortLabel active={materialDetailSortField === 'material'} direction={materialDetailSortField === 'material' ? materialDetailSortDirection : 'asc'} onClick={() => handleMaterialDetailSort('material')}>
                            <strong>Material</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={materialDetailSortField === 'status' ? materialDetailSortDirection : false}>
                          <TableSortLabel active={materialDetailSortField === 'status'} direction={materialDetailSortField === 'status' ? materialDetailSortDirection : 'asc'} onClick={() => handleMaterialDetailSort('status')}>
                            <strong>Status</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={materialDetailSortField === 'productType' ? materialDetailSortDirection : false}>
                          <TableSortLabel active={materialDetailSortField === 'productType'} direction={materialDetailSortField === 'productType' ? materialDetailSortDirection : 'asc'} onClick={() => handleMaterialDetailSort('productType')}>
                            <strong>Product Type</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={materialDetailSortField === 'typeOfWork' ? materialDetailSortDirection : false}>
                          <TableSortLabel active={materialDetailSortField === 'typeOfWork'} direction={materialDetailSortField === 'typeOfWork' ? materialDetailSortDirection : 'asc'} onClick={() => handleMaterialDetailSort('typeOfWork')}>
                            <strong>Type of Work</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={materialDetailSortField === 'size' ? materialDetailSortDirection : false}>
                          <TableSortLabel active={materialDetailSortField === 'size'} direction={materialDetailSortField === 'size' ? materialDetailSortDirection : 'asc'} onClick={() => handleMaterialDetailSort('size')}>
                            <strong>Size</strong>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={materialDetailSortField === 'update' ? materialDetailSortDirection : false}>
                          <TableSortLabel active={materialDetailSortField === 'update'} direction={materialDetailSortField === 'update' ? materialDetailSortDirection : 'asc'} onClick={() => handleMaterialDetailSort('update')}>
                            <strong>Update</strong>
                          </TableSortLabel>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getSortedMaterialDetails(selectedMaterialStatusGroup.details).map((detail) => {
                        const product = products.find(p => p.id === detail.product_id);
                        const productType = productTypes.find(pt => pt.id === detail.product_type_id);
                        const materialName = productType?.productType || 'Unknown';
                        const detailName = getProductDetailName(product, detail);
                        const size = formatSize(detail.width, detail.height);
                        const typeOfWorkValues = detail.type_of_work
                          ? detail.type_of_work.split(',').map((s) => s.trim()).filter(Boolean)
                          : [];
                        const currentStatus = detail.status || 'None';
                        const isChecked = selectedPrintIds.has(detail.id);
                        const updatedAt = detail.updated_at ? formatRelativeTimeInNY(detail.updated_at) : '—';
                        return (
                          <TableRow
                            key={detail.id}
                            selected={isChecked}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={isChecked}
                                onChange={(e) => {
                                  setSelectedPrintIds((prev) => {
                                    const next = new Set(prev);
                                    if (e.target.checked) next.add(detail.id);
                                    else next.delete(detail.id);
                                    return next;
                                  });
                                }}
                              />
                            </TableCell>
                            <TableCell>{detailName}</TableCell>
                            <TableCell>
                              <FormControl size="small" fullWidth>
                                <Select
                                  value={currentStatus}
                                  onChange={(e) => {
                                    const newStatus = e.target.value;
                                    const statusToSave = newStatus === 'None' ? null : newStatus;
                                    if (product) {
                                      handleDetailStatusChange(product.id, detail.id, statusToSave);
                                    }
                                  }}
                                  displayEmpty
                                  disabled={isFieldWorker}
                                >
                                  {detailStatusOptions.map((status) => (
                                    <MenuItem key={status} value={status}>
                                      {status}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </TableCell>
                            <TableCell>{materialName}</TableCell>
                            <TableCell>
                              <Autocomplete
                                multiple
                                size="small"
                                options={typeOfWorkList}
                                value={typeOfWorkValues}
                                onChange={(_, newValue) => {
                                  if (product) {
                                    handleDetailTypeOfWorkChange(product.id, detail.id, newValue);
                                  }
                                }}
                                renderInput={(params) => (
                                  <TextField {...params} placeholder="Select type of work" sx={{ minWidth: 160 }} />
                                )}
                              />
                            </TableCell>
                            <TableCell>{size}</TableCell>
                            <TableCell>{updatedAt}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              
              {/* Mobile Card View */}
              {isSmallScreen && (
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Select All row for mobile */}
                  <Box sx={{ display: 'flex', alignItems: 'center', px: 1 }}>
                    <Checkbox
                      indeterminate={
                        selectedPrintIds.size > 0 &&
                        selectedPrintIds.size < selectedMaterialStatusGroup.details.length
                      }
                      checked={
                        selectedMaterialStatusGroup.details.length > 0 &&
                        selectedPrintIds.size === selectedMaterialStatusGroup.details.length
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedPrintIds(new Set(selectedMaterialStatusGroup.details.map((d) => d.id)));
                        } else {
                          setSelectedPrintIds(new Set());
                        }
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">Select All</Typography>
                  </Box>
                  {getSortedMaterialDetails(selectedMaterialStatusGroup.details).map((detail) => {
                    const product = products.find(p => p.id === detail.product_id);
                    const productType = productTypes.find(pt => pt.id === detail.product_type_id);
                    const materialName = productType?.productType || 'Unknown';
                    const detailName = getProductDetailName(product, detail);
                    const size = formatSize(detail.width, detail.height);
                    const typeOfWorkValues = detail.type_of_work
                      ? detail.type_of_work.split(',').map((s) => s.trim()).filter(Boolean)
                      : [];
                    const currentStatus = detail.status || 'None';
                    const isChecked = selectedPrintIds.has(detail.id);
                    const updatedAt = detail.updated_at ? formatRelativeTimeInNY(detail.updated_at) : '—';
                    return (
                      <Card
                        key={detail.id}
                        sx={{ width: '100%', outline: isChecked ? '2px solid' : 'none', outlineColor: 'primary.main' }}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                  Material
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                  {detailName}
                                </Typography>
                              </Box>
                              <Checkbox
                                checked={isChecked}
                                onChange={(e) => {
                                  setSelectedPrintIds((prev) => {
                                    const next = new Set(prev);
                                    if (e.target.checked) next.add(detail.id);
                                    else next.delete(detail.id);
                                    return next;
                                  });
                                }}
                              />
                            </Box>
                            
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                Status
                              </Typography>
                              <FormControl size="small" fullWidth sx={{ mt: 0.5 }}>
                                <Select
                                  value={currentStatus}
                                  onChange={(e) => {
                                    const newStatus = e.target.value;
                                    const statusToSave = newStatus === 'None' ? null : newStatus;
                                    if (product) {
                                      handleDetailStatusChange(product.id, detail.id, statusToSave);
                                    }
                                  }}
                                  displayEmpty
                                  disabled={isFieldWorker}
                                >
                                  {detailStatusOptions.map((status) => (
                                    <MenuItem key={status} value={status}>
                                      {status}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Box>
                            
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                Product Type
                              </Typography>
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {materialName}
                              </Typography>
                            </Box>
                            
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                Type of Work
                              </Typography>
                              <Autocomplete
                                multiple
                                size="small"
                                options={typeOfWorkList}
                                value={typeOfWorkValues}
                                onChange={(_, newValue) => {
                                  if (product) {
                                    handleDetailTypeOfWorkChange(product.id, detail.id, newValue);
                                  }
                                }}
                                renderInput={(params) => (
                                  <TextField {...params} placeholder="Select type of work" sx={{ mt: 0.5 }} />
                                )}
                              />
                            </Box>
                            
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                Size
                              </Typography>
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {size}
                              </Typography>
                            </Box>

                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                Update
                              </Typography>
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {updatedAt}
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {isSmallScreen ? (
            <Stack direction="column" spacing={1} sx={{ width: '100%', p: 1 }}>
              <Button 
                onClick={handlePrintBarcodes}
                variant="outlined"
                startIcon={<PrintIcon />}
                disabled={!selectedMaterialStatusGroup || selectedMaterialStatusGroup.details.length === 0}
                fullWidth
              >
                {selectedPrintIds.size > 0
                  ? `Print Barcodes (${selectedPrintIds.size})`
                  : 'Print Barcodes'}
              </Button>
              <Button 
                onClick={handleDownloadCSV}
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                disabled={!selectedMaterialStatusGroup || selectedMaterialStatusGroup.details.length === 0}
                fullWidth
              >
                Download CSV
              </Button>
              <Button 
                onClick={handleOpenBulkStatusDialog}
                variant="outlined"
                color="primary"
                disabled={isFieldWorker || !selectedMaterialStatusGroup || selectedMaterialStatusGroup.details.length === 0}
                fullWidth
              >
                Change All Status
              </Button>
              <Button 
                onClick={() => setMaterialDetailDialogOpen(false)}
                variant="contained"
                fullWidth
              >
                Close
              </Button>
            </Stack>
          ) : (
            <>
              <Button 
                onClick={handlePrintBarcodes}
                variant="outlined"
                startIcon={<PrintIcon />}
                disabled={!selectedMaterialStatusGroup || selectedMaterialStatusGroup.details.length === 0}
              >
                {selectedPrintIds.size > 0
                  ? `Print Barcodes (${selectedPrintIds.size})`
                  : 'Print Barcodes'}
              </Button>
              <Button 
                onClick={handleDownloadCSV}
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                disabled={!selectedMaterialStatusGroup || selectedMaterialStatusGroup.details.length === 0}
              >
                Download CSV
              </Button>
              <Button 
                onClick={handleOpenBulkStatusDialog}
                variant="outlined"
                color="primary"
                disabled={isFieldWorker || !selectedMaterialStatusGroup || selectedMaterialStatusGroup.details.length === 0}
              >
                Change All Status
              </Button>
              <Button onClick={() => setMaterialDetailDialogOpen(false)}>Close</Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Bulk Status Change Dialog */}
      <Dialog
        open={openBulkStatusDialog}
        onClose={handleCloseBulkStatusDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change All Statuses</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Alert severity="warning">
              You are about to change the status for all product details in this group. 
              This action will update {selectedMaterialStatusGroup?.details.length || 0} product detail(s).
            </Alert>
            <Autocomplete
              freeSolo
              options={detailStatusOptions}
              value={bulkStatusValue}
              onChange={(_, newValue) => {
                setBulkStatusValue(typeof newValue === 'string' ? newValue : newValue || '');
              }}
              onInputChange={(_, newInputValue) => {
                setBulkStatusValue(newInputValue);
              }}
              filterOptions={(options, params) => {
                const filtered = options.filter((option) =>
                  option.toLowerCase().includes(params.inputValue.toLowerCase())
                );
                return filtered;
              }}
              getOptionLabel={(option) => option || 'None'}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Status"
                  placeholder="Select or type a status..."
                  fullWidth
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBulkStatusDialog} disabled={bulkStatusSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkStatusChange}
            variant="contained"
            disabled={bulkStatusSaving}
            color="warning"
          >
            {bulkStatusSaving ? 'Updating...' : 'Update All Statuses'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
